import { type RefObject, useEffect, useRef, useState } from "react";
import type { ComposerVoiceRecorderHandle } from "../components/Chat/ComposerVoiceRecorder";
import { spliceDictation, useHoldToDictate } from "./useHoldToDictate";

export const VOICE_PLACEHOLDER = "Speak and your words will appear here…";

type Options = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  setValue: (value: string) => void;
  canWrite: boolean;
  onStream: () => void;
  onStart?: (trigger: "start" | "hold") => void;
};

export function useVoiceComposer({
  textareaRef,
  value,
  setValue,
  canWrite,
  onStream,
  onStart,
}: Options) {
  const [isRecording, setIsRecording] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [streamingRange, setStreamingRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const anchorRef = useRef({ before: "", after: "" });
  const recorderRef = useRef<ComposerVoiceRecorderHandle>(null);

  const start = (trigger: "start" | "hold") => {
    onStart?.(trigger);
    const el = textareaRef.current;
    const focused = el !== null && document.activeElement === el;
    anchorRef.current = {
      before: value.slice(0, focused ? el.selectionStart : value.length),
      after: value.slice(focused ? el.selectionEnd : value.length),
    };
    setIsRecording(true);
    setStartedAt(Date.now());
    setElapsed(0);
  };
  const stop = () => {
    setIsRecording(false);
    setStartedAt(null);
    setStreamingRange(null);
  };
  if (!canWrite && isRecording) stop();

  useEffect(() => {
    if (!startedAt) return;
    const interval = setInterval(
      () => setElapsed(Date.now() - startedAt),
      1000
    );
    return () => clearInterval(interval);
  }, [startedAt]);

  const stream = (text: string) => {
    const { before, after } = anchorRef.current;
    const spliced = spliceDictation(before, text, after);
    setValue(spliced.value);
    setStreamingRange({ start: spliced.start, end: spliced.end });
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el && document.activeElement === el)
        el.setSelectionRange(spliced.end, spliced.end);
      onStream();
    });
  };

  useHoldToDictate({
    enabled: canWrite,
    canStart: (e) => !isRecording && e.target === textareaRef.current,
    onHold: () => start("hold"),
    onRelease: () => recorderRef.current?.cancel(),
  });

  return {
    isRecording,
    elapsed,
    streamingRange,
    start: () => start("start"),
    recorder: {
      ref: recorderRef,
      onStream: stream,
      onCancel: (text: string) => {
        stream(text);
        stop();
      },
      onClose: stop,
    },
    finish: (text: string) => {
      stop();
      const { before, after } = anchorRef.current;
      return spliceDictation(before, text, after).value;
    },
  };
}
